import {
  Button,
  CircularProgress,
  FormControl,
  Grid2,
  MenuItem,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { GKDatePicker, GKModal, GKSelect, GKTextField } from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import axiosInstance from "utils/axiosInstance";
import {
  COMPANY_CAP,
  COMPANY_INSTTYPE,
  COMPANY_STATUS,
  INSTRUMENT_SUB_TYPE,
  MUTUAL_FUND_SUB_TYPE,
} from "utils/constants";

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  refetch: any;
}

function CreateCompanyModal(props: Props) {
  const { isModalOpen, setIsModalOpen, refetch } = props;

  const { mutate, isLoading } = useMutation({
    mutationKey: ["CREATE_COMPANY"],
    mutationFn: (data) => axiosInstance.post(`/admin-instrument/create/`, data),
    onSuccess: (res) => {
      toast.success(res.data.message);
      refetch();
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik({
    initialValues: {
      cap: "",
      finCode: "",
      house: "",
      industry: "",
      instType: "",
      kiteCode: "",
      name: "",
      sector: "",
      shortName: "",
      status: "",
      bseCode: "",
      nseCode: "",
      isinCode: "",
      series: "",
      instSubType: "",
      amfiCode: "",
      mfSubType: "",
      startDate: "",
      expiryDate: "",
    },
    onSubmit: (values: any) => mutate(values),
  });

  return (
    <GKModal
      open={isModalOpen}
      setOpen={setIsModalOpen}
      modalTitle={"Add New Company"}
      footer={
        <Grid2 container justifyContent={"flex-end"} gap={1}>
          <Button variant="outlined" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
          <Button type="submit" form="create-company-form" variant="contained">
            {isLoading ? <CircularProgress size={14} /> : "Submit"}
          </Button>
        </Grid2>
      }
    >
      <form onSubmit={formik.handleSubmit} id="create-company-form">
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Name"
                onChange={formik?.handleChange}
                value={formik.values.name}
                name="name"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Sector"
                onChange={formik?.handleChange}
                value={formik.values.sector}
                name="sector"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="ShortName"
                onChange={formik?.handleChange}
                value={formik.values.shortName}
                name="shortName"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Industry"
                onChange={formik?.handleChange}
                value={formik.values.industry}
                name="industry"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="FinCode"
                onChange={formik?.handleChange}
                value={formik.values.finCode}
                name="finCode"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="House"
                onChange={formik?.handleChange}
                value={formik.values.house}
                name="house"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="InstType"
                onChange={formik?.handleChange}
                value={formik.values.instType}
                name="instType"
              >
                {COMPANY_INSTTYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKDatePicker
                inputLabel="Start Date"
                name="startDate"
                value={
                  formik.values.startDate
                    ? dayjs(formik.values.startDate)
                    : null
                }
                onChange={(value) => {
                  if (
                    formik.values.instType === "FUTURES" ||
                    formik.values.instType === "OPTIONS"
                  ) {
                    formik.setFieldValue(
                      "startDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : ""
                    );
                  } else {
                    formik.setFieldValue("startDate", null);
                  }
                }}
                minDate={dayjs("2010-01-01")}
                maxDate={dayjs()}
                disableFuture
                slotProps={{
                  textField: {
                    error: Boolean(
                      formik.errors.startDate && formik.touched.startDate
                    ),
                  },
                }}
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKDatePicker
                inputLabel="End Date"
                name="expiryDate"
                value={
                  formik.values.expiryDate
                    ? dayjs(formik.values.expiryDate)
                    : null
                }
                onChange={(value) => {
                  if (
                    formik.values.instType === "FUTURES" ||
                    formik.values.instType === "OPTIONS"
                  ) {
                    formik.setFieldValue(
                      "expiryDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : ""
                    );
                  } else {
                    formik.setFieldValue("expiryDate", null);
                  }
                }}
                minDate={dayjs("2010-01-01")}
                maxDate={dayjs()}
                disableFuture
                slotProps={{
                  textField: {
                    error: Boolean(
                      formik.errors.expiryDate && formik.touched.expiryDate
                    ),
                  },
                }}
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="KiteCode"
                onChange={formik?.handleChange}
                value={formik.values.kiteCode}
                name="kiteCode"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Cap"
                onChange={formik?.handleChange}
                value={formik.values.cap}
                name="cap"
              >
                {COMPANY_CAP.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Status"
                onChange={formik?.handleChange}
                value={formik.values.status}
                name="status"
              >
                {COMPANY_STATUS.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="BSE"
                onChange={formik?.handleChange}
                value={formik.values.bseCode}
                name="bseCode"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="ISIN"
                onChange={formik?.handleChange}
                value={formik.values.isinCode}
                name="isinCode"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="NSE"
                onChange={formik?.handleChange}
                value={formik.values.nseCode}
                name="nseCode"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Series"
                onChange={formik?.handleChange}
                value={formik.values.series}
                name="series"
              />
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Mutual Funds Sub Type"
                onChange={formik?.handleChange}
                value={formik.values.mfSubType}
                name="mfSubType"
              >
                {MUTUAL_FUND_SUB_TYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="Instrument Sub Type"
                onChange={formik?.handleChange}
                value={formik.values.instSubType}
                name="instSubType"
              >
                {INSTRUMENT_SUB_TYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="AMFI Code"
                onChange={formik?.handleChange}
                value={formik.values.amfiCode}
                name="amfiCode"
              />
            </FormControl>
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
}

export default CreateCompanyModal;
